import {
    ArrowPathIcon,
    CheckCircleIcon,
    ClockIcon,
    CreditCardIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { differenceInDays, format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const MembershipManagement = () => {
  const [agentMemberships, setAgentMemberships] = useState([]);
  const [sellerMemberships, setSellerMemberships] = useState([]);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [planDrafts, setPlanDrafts] = useState({});
  const [savingPlanId, setSavingPlanId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('agents'); // 'agents' or 'sellers'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'expired'

  // Whether listing limits are actually enforced. With this off, allowances
  // are measured and shown to sellers but never block anything - which is how
  // the platform ran during its free trial.
  const [billingEnabled, setBillingEnabled] = useState(null);
  const [savingBilling, setSavingBilling] = useState(false);

  // Assigning a plan by hand - onboarding terms, a trial, or correcting
  // something a payment got wrong.
  const [planDialog, setPlanDialog] = useState(null);
  const [planChoice, setPlanChoice] = useState('agent');
  const [planMonths, setPlanMonths] = useState(3);
  const [planNote, setPlanNote] = useState('');
  const [applyingPlan, setApplyingPlan] = useState(false);

  const openPlanDialog = (user) => {
    setPlanDialog(user);
    setPlanChoice(user?.membership?.type || 'basic');
    setPlanMonths(3);
    setPlanNote('');
  };

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
  });

  const applyPlanAction = async (action) => {
    if (!planDialog) return;
    setApplyingPlan(true);

    const base = `${process.env.REACT_APP_API_URL}/api/admin/users/${planDialog._id}/plan`;

    try {
      let res;
      if (action === 'set') {
        res = await axios.patch(base, {
          planName: planChoice,
          months: planMonths,
          note: planNote,
        }, authHeader());
      } else if (action === 'extend') {
        res = await axios.patch(`${base}/extend`, {
          months: planMonths,
          note: planNote,
        }, authHeader());
      } else {
        res = await axios.patch(`${base}/reset`, { note: planNote }, authHeader());
      }

      toast.success(res.data?.message || 'Plan updated');
      setPlanDialog(null);
      fetchMemberships();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not update the plan');
    } finally {
      setApplyingPlan(false);
    }
  };

  useEffect(() => {
    fetchMemberships();
    loadBillingSetting();
  }, []);

  const loadBillingSetting = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/billing`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      );
      setBillingEnabled(Boolean(res.data?.data?.billingEnabled));
    } catch (error) {
      setBillingEnabled(null);
    }
  };

  const toggleBilling = async () => {
    const next = !billingEnabled;

    const confirmText = next
      ? 'Turn billing on? Sellers who have used their allowance will not be able to add more listings until they upgrade.'
      : 'Turn billing off? Listing limits will be shown but not enforced.';

    if (!window.confirm(confirmText)) return;

    setSavingBilling(true);
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/billing`,
        { billingEnabled: next },
        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      );
      setBillingEnabled(Boolean(res.data?.data?.billingEnabled));
      toast.success(res.data?.message || 'Billing updated');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not change billing');
    } finally {
      setSavingBilling(false);
    }
  };


  const fetchMemberships = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [agentsRes, sellersRes, plansRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/agents`, config),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/sellers?sellerType=individual`, config),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/membership-plans`, config),
      ]);

      // Handle nested response structure
      const agentsData = agentsRes.data.data || agentsRes.data;
      const sellersData = sellersRes.data.data || sellersRes.data;
      const plansData = plansRes.data.data || plansRes.data;
      
      setAgentMemberships(Array.isArray(agentsData) ? agentsData : []);
      setSellerMemberships(Array.isArray(sellersData) ? sellersData : []);

      const safePlans = Array.isArray(plansData) ? plansData : [];
      setMembershipPlans(safePlans);

      const nextDrafts = {};
      safePlans.forEach((plan) => {
        nextDrafts[plan._id] = {
          monthly: String(plan.pricing?.monthly ?? 0),
          quarterly: String(plan.pricing?.quarterly ?? 0),
          annually: String(plan.pricing?.annually ?? 0),
          maxListings: String(plan.features?.maxListings ?? 0),
          maxSellers: String(plan.features?.maxSellers ?? 0),
          featureHighlightsText: Array.isArray(plan.featureHighlights)
            ? plan.featureHighlights.join('\n')
            : '',
        };
      });
      setPlanDrafts(nextDrafts);
    } catch (error) {
      toast.error('Failed to load memberships');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanDraftChange = (planId, field, value) => {
    setPlanDrafts((prev) => ({
      ...prev,
      [planId]: {
        ...(prev[planId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSavePlan = async (planId) => {
    const draft = planDrafts[planId];
    if (!draft) {
      return;
    }

    const monthly = Number(draft.monthly ?? 0);
    const quarterly = Number(draft.quarterly ?? 0);
    const annually = Number(draft.annually ?? 0);

    if ([monthly, quarterly, annually].some((price) => !Number.isFinite(price) || price < 0)) {
      toast.error('Please enter valid non-negative pricing values');
      return;
    }

    // -1 means unlimited; anything below that is meaningless.
    const maxListings = Number(draft.maxListings ?? 0);
    const maxSellers = Number(draft.maxSellers ?? 0);

    if ([maxListings, maxSellers].some((v) => !Number.isInteger(v) || v < -1)) {
      toast.error('Limits must be whole numbers, or -1 for unlimited');
      return;
    }

    const featureHighlights = String(draft.featureHighlightsText || '')
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

    try {
      setSavingPlanId(planId);
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/membership-plans/${planId}`,
        {
          pricing: { monthly, quarterly, annually },
          features: { maxListings, maxSellers },
          featureHighlights,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedPlan = response.data?.data;
      if (updatedPlan?._id) {
        setMembershipPlans((prev) =>
          prev.map((plan) => (plan._id === updatedPlan._id ? updatedPlan : plan))
        );

        setPlanDrafts((prev) => ({
          ...prev,
          [updatedPlan._id]: {
            monthly: String(updatedPlan.pricing?.monthly ?? 0),
            quarterly: String(updatedPlan.pricing?.quarterly ?? 0),
            annually: String(updatedPlan.pricing?.annually ?? 0),
            maxListings: String(updatedPlan.features?.maxListings ?? 0),
            maxSellers: String(updatedPlan.features?.maxSellers ?? 0),
            featureHighlightsText: Array.isArray(updatedPlan.featureHighlights)
              ? updatedPlan.featureHighlights.join('\n')
              : '',
          },
        }));
      }

      toast.success('Membership plan updated');
    } catch (error) {
      console.error('Failed to update membership plan:', error);
      toast.error(error?.response?.data?.message || 'Failed to update membership plan');
    } finally {
      setSavingPlanId(null);
    }
  };

  const formatPlanName = (planName = '') => {
    const value = String(planName);
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const getMembershipStatus = (membership) => {
    if (!membership) return 'inactive';
    const endDate = new Date(membership.endDate);
    const now = new Date();
    
    if (endDate < now) return 'expired';
    if (membership.isActive) return 'active';
    return 'inactive';
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    return differenceInDays(end, now);
  };

  const getMembershipBadge = (membership) => {
    const status = getMembershipStatus(membership);
    
    if (status === 'active') {
      const daysLeft = getDaysRemaining(membership.endDate);
      if (daysLeft < 7) {
        return { text: `${daysLeft} days left`, color: 'yellow' };
      }
      return { text: 'Active', color: 'green' };
    } else if (status === 'expired') {
      return { text: 'Expired', color: 'red' };
    }
    return { text: 'Inactive', color: 'gray' };
  };

  const getMembershipTypeBadge = (type) => {
    const types = {
      'free': { text: 'Free Trial', color: 'blue' },
      'basic': { text: 'Basic', color: 'gray' },
      'pro': { text: 'Pro', color: 'orange' },
      'premium': { text: 'Premium', color: 'purple' },
      'agent': { text: 'Agent', color: 'purple' }
    };
    return types[type?.toLowerCase()] || types.free;
  };

  const filteredData = (data) => {
    let filtered = data.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      const fullName = item.firstName && item.lastName 
        ? `${item.firstName} ${item.lastName}`.toLowerCase()
        : (item.name?.toLowerCase() || item.fullName?.toLowerCase() || '');
      const email = item.email?.toLowerCase() || '';
      return fullName.includes(searchLower) || email.includes(searchLower);
    });

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => {
        const status = getMembershipStatus(item.membership);
        return status === filterStatus;
      });
    }

    return filtered;
  };

  const currentData = activeTab === 'agents' 
    ? filteredData(agentMemberships) 
    : filteredData(sellerMemberships);

  const stats = {
    agents: {
      total: agentMemberships.length,
      active: agentMemberships.filter(a => getMembershipStatus(a.membership) === 'active').length,
      expired: agentMemberships.filter(a => getMembershipStatus(a.membership) === 'expired').length,
      inactive: agentMemberships.filter(a => getMembershipStatus(a.membership) === 'inactive').length
    },
    sellers: {
      total: sellerMemberships.length,
      active: sellerMemberships.filter(s => getMembershipStatus(s.membership) === 'active').length,
      expired: sellerMemberships.filter(s => getMembershipStatus(s.membership) === 'expired').length,
      inactive: sellerMemberships.filter(s => getMembershipStatus(s.membership) === 'inactive').length
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900">
            Membership Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage agent and seller memberships with 3-month free trial tracking
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={fetchMemberships}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </motion.button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Memberships</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats[activeTab].total}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <CreditCardIcon className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {stats[activeTab].active}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Expired</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {stats[activeTab].expired}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircleIcon className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Inactive</p>
              <p className="text-2xl font-bold text-gray-600 mt-1">
                {stats[activeTab].inactive}
              </p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Billing switch */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-[280px]">
            <h2 className="text-xl font-semibold text-gray-900">Billing</h2>
            <p className="text-sm text-gray-600 mt-1">
              {billingEnabled
                ? 'Listing limits are being enforced. A seller who reaches their allowance cannot add more until they upgrade.'
                : 'Limits are shown to sellers but not enforced, so everyone can list without restriction.'}
            </p>
          </div>

          <button
            type="button"
            onClick={toggleBilling}
            disabled={savingBilling || billingEnabled === null}
            className={`px-5 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-60 ${
              billingEnabled
                ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {savingBilling
              ? 'Saving...'
              : billingEnabled === null
                ? 'Unavailable'
                : billingEnabled ? 'Turn billing off' : 'Turn billing on'}
          </button>
        </div>

        <div className={`mt-4 px-4 py-3 rounded-lg text-sm ${
          billingEnabled ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'
        }`}>
          Currently <strong>{billingEnabled === null ? 'unknown' : billingEnabled ? 'ON' : 'OFF'}</strong>
          {billingEnabled ? '' : ' \u2014 every seller can list without limit'}
        </div>
      </div>

      {/* Membership Plan Editor */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Membership Plan Pricing & Features</h2>
          <p className="text-sm text-gray-600 mt-1">
            Update plan prices and feature highlights shown in seller membership screens.
          </p>
        </div>

        {membershipPlans.length === 0 ? (
          <div className="text-sm text-gray-500 py-4">No membership plans available.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {membershipPlans.map((plan) => {
              const draft = planDrafts[plan._id] || {
                monthly: '0',
                quarterly: '0',
                annually: '0',
                maxListings: '0',
                maxSellers: '0',
                featureHighlightsText: '',
              };
              const isAgentPlan = plan.targetAudience === 'agent';

              return (
                <div key={plan._id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{formatPlanName(plan.displayName || plan.name)}</h3>
                    <p className="text-xs text-gray-500 mt-1">Plan code: {plan.name}</p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <label className="text-xs text-gray-600">
                      Monthly
                      <input
                        type="number"
                        min="0"
                        value={draft.monthly}
                        onChange={(e) => handlePlanDraftChange(plan._id, 'monthly', e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </label>
                    <label className="text-xs text-gray-600">
                      Quarterly
                      <input
                        type="number"
                        min="0"
                        value={draft.quarterly}
                        onChange={(e) => handlePlanDraftChange(plan._id, 'quarterly', e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </label>
                    <label className="text-xs text-gray-600">
                      Annually
                      <input
                        type="number"
                        min="0"
                        value={draft.annually}
                        onChange={(e) => handlePlanDraftChange(plan._id, 'annually', e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </label>
                  </div>

                  {/* Limits define what the plan actually grants. -1 = unlimited. */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <label className="text-xs text-gray-600">
                      Listings / 30 days
                      <input
                        type="number"
                        min="-1"
                        value={draft.maxListings}
                        onChange={(e) => handlePlanDraftChange(plan._id, 'maxListings', e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </label>
                    <label className={`text-xs ${isAgentPlan ? 'text-gray-600' : 'text-gray-400'}`}>
                      Max sellers
                      <input
                        type="number"
                        min="-1"
                        value={draft.maxSellers}
                        onChange={(e) => handlePlanDraftChange(plan._id, 'maxSellers', e.target.value)}
                        disabled={!isAgentPlan}
                        title={isAgentPlan ? '' : 'Only agent plans can onboard sellers'}
                        className="mt-1 w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </label>
                  </div>

                  <label className="text-xs text-gray-600 block">
                    Features (one per line)
                    <textarea
                      rows={5}
                      value={draft.featureHighlightsText}
                      onChange={(e) => handlePlanDraftChange(plan._id, 'featureHighlightsText', e.target.value)}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter one feature per line"
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() => handleSavePlan(plan._id)}
                    disabled={savingPlanId === plan._id}
                    className="mt-3 w-full px-3 py-2 rounded-md text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {savingPlanId === plan._id ? 'Saving...' : 'Save Plan'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('agents')}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'agents'
                  ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Agent Memberships
            </button>
            <button
              onClick={() => setActiveTab('sellers')}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'sellers'
                  ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Individual Seller Memberships
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-300">
            <FunnelIcon className="w-5 h-5 text-gray-600" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-transparent border-none focus:outline-none text-gray-700 font-medium cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : currentData.length === 0 ? (
          <div className="text-center py-20">
            <CreditCardIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No memberships found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Membership Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Start Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">End Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Free Trial</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Plan</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {currentData.map((item, index) => {
                    const statusBadge = getMembershipBadge(item.membership);
                    const typeBadge = getMembershipTypeBadge(item.membership?.type);
                    const isFreeTrialActive = item.membership?.type?.toLowerCase() === 'free' && 
                                            getMembershipStatus(item.membership) === 'active';

                    return (
                      <motion.tr
                        key={item._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 px-4">
                          <div className="font-medium text-gray-900">
                            {item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : (item.name || item.fullName || 'N/A')}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-600">
                          {item.email}
                        </td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold
                            ${typeBadge.color === 'blue' ? 'bg-blue-100 text-blue-700' : ''}
                            ${typeBadge.color === 'gray' ? 'bg-gray-100 text-gray-700' : ''}
                            ${typeBadge.color === 'orange' ? 'bg-orange-100 text-orange-700' : ''}
                            ${typeBadge.color === 'purple' ? 'bg-purple-100 text-purple-700' : ''}
                          `}>
                            {typeBadge.text}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-gray-600">
                          {item.membership?.startDate 
                            ? format(new Date(item.membership.startDate), 'MMM dd, yyyy')
                            : 'N/A'}
                        </td>
                        <td className="py-4 px-4 text-gray-600">
                          {item.membership?.endDate 
                            ? format(new Date(item.membership.endDate), 'MMM dd, yyyy')
                            : 'N/A'}
                        </td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold
                            ${statusBadge.color === 'green' ? 'bg-green-100 text-green-700' : ''}
                            ${statusBadge.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' : ''}
                            ${statusBadge.color === 'red' ? 'bg-red-100 text-red-700' : ''}
                            ${statusBadge.color === 'gray' ? 'bg-gray-100 text-gray-700' : ''}
                          `}>
                            {statusBadge.text}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          {isFreeTrialActive ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                              ✓ Active (3 months)
                            </span>
                          ) : item.membership?.type?.toLowerCase() === 'free' ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                              Completed
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">N/A</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <button
                            type="button"
                            onClick={() => openPlanDialog(item)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-primary-300 text-primary-700 hover:bg-primary-50"
                          >
                            Change
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Assign or extend a plan */}
      <AnimatePresence>
        {planDialog ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={() => setPlanDialog(null)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900">Change plan</h3>
              <p className="text-sm text-gray-600 mt-1">
                {planDialog.firstName} {planDialog.lastName} \u00B7 {planDialog.email}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Currently on <strong>{planDialog.membership?.type || 'basic'}</strong>
              </p>

              <label className="block text-sm text-gray-700 mt-5">
                Plan
                <select
                  value={planChoice}
                  onChange={(e) => setPlanChoice(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {membershipPlans.map((p) => (
                    <option key={p._id} value={p.name}>
                      {p.displayName || p.name}
                      {p.features?.maxListings ? ` \u2014 ${p.features.maxListings} listings` : ''}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-sm text-gray-700 mt-4">
                Months
                <input
                  type="number"
                  min="1"
                  max="36"
                  value={planMonths}
                  onChange={(e) => setPlanMonths(Number(e.target.value))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </label>

              <label className="block text-sm text-gray-700 mt-4">
                Note (optional)
                <input
                  type="text"
                  value={planNote}
                  onChange={(e) => setPlanNote(e.target.value)}
                  placeholder="Why this was granted"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </label>

              <div className="flex flex-wrap gap-2 mt-6">
                <button
                  type="button"
                  disabled={applyingPlan}
                  onClick={() => applyPlanAction('set')}
                  className="flex-1 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-semibold disabled:opacity-60"
                >
                  Grant plan
                </button>
                <button
                  type="button"
                  disabled={applyingPlan}
                  onClick={() => applyPlanAction('extend')}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold disabled:opacity-60"
                >
                  Extend
                </button>
              </div>

              <button
                type="button"
                disabled={applyingPlan}
                onClick={() => applyPlanAction('reset')}
                className="w-full mt-2 px-4 py-2.5 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 disabled:opacity-60"
              >
                Move to Free plan
              </button>

              <p className="text-xs text-gray-500 mt-3">
                Existing listings are never removed by any of these actions.
              </p>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

    </div>
  );
};

export default MembershipManagement;
